import * as React from "react";
import { Box } from "../../components/ui/box";
import type { Omittable } from "../../lib/props";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

type HsvaColor = { h: number; s: number; v: number; a: number };

export type ColorPickerProps = Omittable<{
  /** HSL string value in theme-token format: "H S% L%", e.g. "248 76% 60%". */
  value: string;
  /** Called with the new HSL string as the user drags. */
  onChange: (hsl: string) => void;
  /** Width/height of the saturation area in pixels. Defaults to 200. */
  size?: number;
}>;

// ---------------------------------------------------------------------------
// Color math
// ---------------------------------------------------------------------------

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function parseHsl(hsl: string): { h: number; s: number; l: number } {
  const parts = hsl.replace(/%/g, "").split(/\s+/).map(Number);
  return { h: parts[0] || 0, s: parts[1] || 0, l: parts[2] || 0 };
}

function formatHsl(h: number, s: number, l: number): string {
  return `${Math.round(h)} ${Math.round(s)}% ${Math.round(l)}%`;
}

function hslaToHsva(h: number, s: number, l: number): HsvaColor {
  s /= 100;
  l /= 100;
  const v = l + s * Math.min(l, 1 - l);
  const sv = v === 0 ? 0 : 2 * (1 - l / v);
  return { h, s: sv * 100, v: v * 100, a: 1 };
}

function hsvaToHsla(hsva: HsvaColor): { h: number; s: number; l: number } {
  const { h, s, v } = hsva;
  const hh = ((200 - s) * v) / 100;
  return {
    h: Math.round(h),
    s: Math.round(
      hh > 0 && hh < 200
        ? ((s * v) / 100 / (hh <= 100 ? hh : 200 - hh)) * 100
        : 0,
    ),
    l: Math.round(hh / 2),
  };
}

function hsvaToHslString(hsva: HsvaColor): string {
  const { h, s, l } = hsvaToHsla(hsva);
  return `hsl(${h}, ${Math.round(s)}%, ${Math.round(l)}%)`;
}

// ---------------------------------------------------------------------------
// Interaction hook — pointer drag on a rectangular area, with pointer capture.
// Reports normalized { left: 0..1, top: 0..1 }.
// ---------------------------------------------------------------------------

function useInteraction(
  onMove: (pos: { left: number; top: number }) => void,
  onDragStart?: () => void,
  onDragEnd?: () => void,
) {
  const containerRef = React.useRef<HTMLDivElement>(null);

  const getPosition = React.useCallback(
    (e: PointerEvent) => {
      const rect = containerRef.current?.getBoundingClientRect();
      if (!rect) return;
      onMove({
        left: clamp((e.clientX - rect.left) / rect.width, 0, 1),
        top: clamp((e.clientY - rect.top) / rect.height, 0, 1),
      });
    },
    [onMove],
  );

  const onPointerDown = React.useCallback(
    (e: React.PointerEvent) => {
      e.preventDefault();
      const el = containerRef.current;
      if (!el) return;
      el.setPointerCapture(e.pointerId);
      onDragStart?.();
      getPosition(e.nativeEvent);
    },
    [getPosition, onDragStart],
  );

  const onPointerMove = React.useCallback(
    (e: React.PointerEvent) => {
      if (e.buttons > 0) {
        getPosition(e.nativeEvent);
      }
    },
    [getPosition],
  );

  const onPointerEnd = React.useCallback(() => {
    onDragEnd?.();
  }, [onDragEnd]);

  return { containerRef, onPointerDown, onPointerMove, onPointerEnd };
}

// ---------------------------------------------------------------------------
// Pointer (the draggable circle)
// ---------------------------------------------------------------------------

const POINTER_SIZE = 20;

function Pointer({
  left,
  top,
  color,
}: {
  left: number;
  top: number;
  color: string;
}) {
  return (
    <Box
      data-slot="color-picker-pointer"
      position="absolute"
      rounded="full"
      width={POINTER_SIZE}
      height={POINTER_SIZE}
      // Dynamic drag position and live swatch color — style is the only channel
      // for computed values; the static ring/shadow ride along.
      style={{
        left: `${left * 100}%`,
        top: `${top * 100}%`,
        transform: "translate(-50%, -50%)",
        backgroundColor: color,
        border: "2px solid #fff",
        boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
        pointerEvents: "none",
      }}
    />
  );
}

// ---------------------------------------------------------------------------
// Saturation area — 2D picker for saturation (X) and value/brightness (Y)
// ---------------------------------------------------------------------------

function SaturationArea({
  hsva,
  size,
  onChange,
  onDragStart,
  onDragEnd,
}: Omittable<{
  hsva: HsvaColor;
  size: number;
  onChange: (partial: Partial<HsvaColor>) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}>) {
  const onMove = React.useCallback(
    (pos: { left: number; top: number }) => {
      onChange({ s: pos.left * 100, v: (1 - pos.top) * 100 });
    },
    [onChange],
  );

  const { containerRef, onPointerDown, onPointerMove, onPointerEnd } =
    useInteraction(onMove, onDragStart, onDragEnd);

  const left = hsva.s / 100;
  const top = 1 - hsva.v / 100;
  const displayColor = hsvaToHslString(hsva);

  return (
    <Box
      ref={containerRef}
      data-slot="color-picker-saturation"
      position="relative"
      rounded="md"
      cursor="crosshair"
      width={size}
      height={size}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      style={{
        backgroundImage:
          "linear-gradient(to top, #000, transparent), linear-gradient(to right, #fff, transparent)",
        backgroundColor: `hsl(${hsva.h}, 100%, 50%)`,
        touchAction: "none",
      }}
    >
      <Pointer left={left} top={top} color={displayColor} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Hue slider — horizontal rainbow strip
// ---------------------------------------------------------------------------

const HUE_HEIGHT = 16;

function HueSlider({
  hue,
  width,
  onChange,
  onDragStart,
  onDragEnd,
}: Omittable<{
  hue: number;
  width: number;
  onChange: (partial: Partial<HsvaColor>) => void;
  onDragStart?: () => void;
  onDragEnd?: () => void;
}>) {
  const onMove = React.useCallback(
    (pos: { left: number }) => {
      onChange({ h: pos.left * 360 });
    },
    [onChange],
  );

  const { containerRef, onPointerDown, onPointerMove, onPointerEnd } =
    useInteraction(onMove, onDragStart, onDragEnd);
  const left = hue / 360;
  const displayColor = `hsl(${Math.round(hue)}, 100%, 50%)`;

  return (
    <Box
      ref={containerRef}
      data-slot="color-picker-hue"
      position="relative"
      rounded="full"
      cursor="crosshair"
      width={width}
      height={HUE_HEIGHT}
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerEnd}
      onPointerCancel={onPointerEnd}
      style={{
        background:
          "linear-gradient(to right, #f00 0%, #ff0 17%, #0f0 33%, #0ff 50%, #00f 67%, #f0f 83%, #f00 100%)",
        touchAction: "none",
      }}
    >
      <Pointer left={left} top={0.5} color={displayColor} />
    </Box>
  );
}

// ---------------------------------------------------------------------------
// ColorPicker
// ---------------------------------------------------------------------------

/**
 * Inline free-color picker: a saturation/value area over a hue slider.
 * Accepts and emits HSL strings in theme-token format ("H S% L%") — the
 * caller owns any conversion to a paintable color.
 */
export function ColorPicker({ value, onChange, size = 200 }: ColorPickerProps) {
  const parsed = React.useMemo(() => parseHsl(value), [value]);
  const [hsva, setHsva] = React.useState<HsvaColor>(() =>
    hslaToHsva(parsed.h, parsed.s, parsed.l),
  );
  const isDragging = React.useRef(false);
  const isInitialized = React.useRef(false);

  // Sync from external value changes — skip while dragging to avoid a lossy
  // HSL↔HSV round-trip overwriting precise internal state mid-gesture. The
  // no-op guard matters: an unconditional set would swap in an equal-but-new
  // hsva object on mount and fire a spurious initial onChange.
  React.useEffect(() => {
    if (isDragging.current) return;
    setHsva((prev) => {
      const next = hslaToHsva(parsed.h, parsed.s, parsed.l);
      return next.h === prev.h && next.s === prev.s && next.v === prev.v
        ? prev
        : next;
    });
  }, [parsed.h, parsed.s, parsed.l]);

  const onChangeRef = React.useRef(onChange);
  onChangeRef.current = onChange;

  // Emit onChange when hsva changes, skipping the initial render.
  React.useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    const { h, s, l } = hsvaToHsla(hsva);
    onChangeRef.current(formatHsl(h, s, l));
  }, [hsva]);

  const handleChange = React.useCallback((partial: Partial<HsvaColor>) => {
    setHsva((prev) => ({ ...prev, ...partial }));
  }, []);

  const handleDragStart = React.useCallback(() => {
    isDragging.current = true;
  }, []);

  const handleDragEnd = React.useCallback(() => {
    isDragging.current = false;
  }, []);

  return (
    <Box userSelect="none"
      data-slot="color-picker"
      direction="column"
      gap="md"
    >
      <SaturationArea
        hsva={hsva}
        size={size}
        onChange={handleChange}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />
      <HueSlider
        hue={hsva.h}
        width={size}
        onChange={handleChange}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      />
    </Box>
  );
}
